/**
 * The second reader's retry spacing (86d3yeh4j) — ONE definition, because the operator is shown a
 * number derived from it.
 *
 * ## Why this file exists rather than a sentence in the dialog
 *
 * The setting is "how many times do we try before a person has to look at it", and the only way to
 * make that number mean anything is to say how long it buys: a slider that says "4" without saying
 * "about twelve minutes" is asking someone to tune a thing they cannot feel. So the dialog prints a
 * duration — and the instant a duration is printed, it can be WRONG.
 *
 * It nearly was. The approved copy said "about 20 minutes" at four tries, taken from a schedule of
 * 1/4/7/10 minutes read as four waits. Four tries is four READS and therefore only three waits
 * (1 + 4 + 7 = 12), so the sentence overstated the wait by 67% — and a hardcoded string would have
 * kept overstating it forever, including after any future change to the server's schedule. A copy
 * that lies about how long a scan waits is worse than no copy: it is what an operator uses to decide
 * whether something is stuck.
 *
 * ⚠️ MIRRORS `DocScanVerifyHandler.TransientRetryDelaysSeconds`. If the server's schedule changes,
 * change it HERE in the same commit — this is the only place the UI may derive the wait from.
 */

/** Seconds to wait before try N+1, indexed by tries already spent. Server: `[60, 240, 420, 600]`. */
export const TRANSIENT_RETRY_DELAYS_SECONDS = [60, 240, 420, 600] as const;

/** Bounds of `maxVerificationAttempts` — matches the server's FluentValidation rule (1–6). */
export const MIN_VERIFICATION_ATTEMPTS = 1;
export const MAX_VERIFICATION_ATTEMPTS = 6;

/** The shipped default, used only to describe the control — the live value comes from the server. */
export const DEFAULT_VERIFICATION_ATTEMPTS = 4;

/**
 * Total seconds a scan spends waiting between tries, at `tries` total tries.
 *
 * N tries means N−1 GAPS, which is the whole arithmetic the approved copy got wrong. Beyond the
 * schedule's length the last delay repeats, exactly as the server clamps its index.
 */
export function retryWindowSeconds(tries: number): number {
  const gaps = Math.max(0, Math.floor(tries) - 1);
  let total = 0;
  for (let i = 0; i < gaps; i += 1) {
    total += TRANSIENT_RETRY_DELAYS_SECONDS[Math.min(i, TRANSIENT_RETRY_DELAYS_SECONDS.length - 1)];
  }
  return total;
}

/**
 * The same window in whole minutes, for the live echo. Rounded, never floored: at two tries the wait
 * is 60 s, and "about 1 minute" is the truth while "about 0 minutes" reads as "instant".
 */
export function retryWindowMinutes(tries: number): number {
  return Math.round(retryWindowSeconds(tries) / 60);
}
